/** Ebook formats Shelflist accepts, with their file extensions and MIME types. */
export const BOOK_FORMATS = [
  "epub",
  "kepub",
  "pdf",
  "mobi",
  "azw3",
  "azw",
  "fb2",
  "cbz",
  "cbr",
  "txt",
] as const;

export type BookFormat = (typeof BOOK_FORMATS)[number];

export const FORMAT_MIME_TYPES: Record<BookFormat, string> = {
  epub: "application/epub+zip",
  kepub: "application/epub+zip",
  pdf: "application/pdf",
  mobi: "application/x-mobipocket-ebook",
  azw3: "application/vnd.amazon.ebook",
  azw: "application/vnd.amazon.ebook",
  fb2: "application/x-fictionbook+xml",
  cbz: "application/vnd.comicbook+zip",
  cbr: "application/vnd.comicbook-rar",
  txt: "text/plain",
};

/** Extensions (with leading dot, lowercase) accepted by the upload; `.kepub.epub` is handled separately. */
export const UPLOAD_EXTENSIONS = [...BOOK_FORMATS.map((format) => `.${format}`), ".kepub.epub"];

export function isBookFormat(value: unknown): value is BookFormat {
  return typeof value === "string" && (BOOK_FORMATS as readonly string[]).includes(value);
}

/**
 * Determines the format from a filename: case-insensitive extension, with
 * `.kepub.epub` and `.kepub` meaning `kepub`. Returns undefined for unknown extensions.
 */
export function formatFromFilename(filename: string): BookFormat | undefined {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".kepub.epub")) return "kepub";
  const dot = lower.lastIndexOf(".");
  if (dot < 0) return undefined;
  const extension = lower.slice(dot + 1);
  return isBookFormat(extension) ? extension : undefined;
}

/** File extension used for stored files (`kepub` is stored as `kepub.epub`). */
export function storageExtension(format: BookFormat): string {
  return format === "kepub" ? "kepub.epub" : format;
}

/** `Content-Type` of downloaded files (differs from {@link FORMAT_MIME_TYPES}, which feeds file pickers). */
export const DOWNLOAD_MIME_TYPES: Record<BookFormat, string> = {
  epub: "application/epub+zip",
  kepub: "application/epub+zip",
  pdf: "application/pdf",
  mobi: "application/x-mobipocket-ebook",
  azw3: "application/vnd.amazon.mobi8-ebook",
  azw: "application/vnd.amazon.ebook",
  fb2: "application/x-fictionbook+xml",
  cbz: "application/vnd.comicbook+zip",
  cbr: "application/vnd.comicbook-rar",
  txt: "text/plain; charset=utf-8",
};
